import { useState, useEffect } from 'react';
import { islamiyahAPI } from '../../utils/api';
import PublicLayout from '../../components/layout/PublicLayout';
import { MapPinIcon, PhoneIcon, EnvelopeIcon, AcademicCapIcon, UserIcon } from '@heroicons/react/24/outline';
import useSEO from '../../hooks/useSEO';

export default function IslamiyahPage() {
  useSEO({
    title: 'JNF Islamiyah — Schools',
    description: 'Learn about JNF Islamiyah — its history, mission, and branch locations across Nigeria.',
  });

  const [info, setInfo]         = useState(null);
  const [branches, setBranches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('about'); // 'about' | 'branches'

  useEffect(() => {
    Promise.all([
      islamiyahAPI.getInfo(),
      islamiyahAPI.getBranches(),
    ]).then(([i, b]) => {
      setInfo(i.data.data || null);
      const bList = b.data.data || [];
      setBranches(bList);
      setSelected(bList[0] || null);
    }).finally(() => setLoading(false));
  }, []);

  // Group branches by city for the sidebar list
  const cities = [...new Set(branches.map(b => b.city).filter(Boolean))];

  return (
    <PublicLayout>

      {/* ── Hero ─────────────────────────────────────────────── */}
      {info?.cover_url ? (
        <div className="relative w-full h-64 md:h-80 overflow-hidden">
          <img src={info.cover_url} alt="JNF Islamiyah" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900/85 via-navy-900/40 to-transparent flex items-end">
            <div className="max-w-7xl mx-auto px-4 pb-8 w-full">
              <p className="text-cyan-400 text-xs font-semibold uppercase tracking-widest mb-1">✦ Seeking Knowledge is Obligatory ✦</p>
              <h1 className="text-white text-3xl md:text-4xl font-bold leading-tight">
                {info?.school_name || 'JNF Islamiyah'}
              </h1>
              {info?.tagline && <p className="text-white/70 text-sm mt-1">{info.tagline}</p>}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-navy-800 to-navy-950 py-16 px-4 text-center">
          <p className="text-cyan-400 text-xs font-semibold uppercase tracking-widest mb-3">✦ Seeking Knowledge is Obligatory ✦</p>
          <h1 className="text-white text-3xl md:text-4xl font-bold mb-2">
            {info?.school_name || 'JNF Islamiyah'}
          </h1>
          {info?.tagline && <p className="text-white/60 text-sm mt-1 max-w-xl mx-auto">{info.tagline}</p>}
          <p className="arabic text-cyan-300 text-lg mt-4">طَلَبُ الْعِلْمِ فَرِيضَةٌ عَلَى كُلِّ مُسْلِمٍ</p>
        </div>
      )}

      {/* ── Tab strip ────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-100 sticky top-16 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-0">
            {[
              { key: 'about',    label: 'About'    },
              { key: 'branches', label: `Branches${branches.length > 0 ? ` (${branches.length})` : ''}` },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-6 py-4 text-sm font-semibold border-b-2 transition-all ${
                  activeTab === key
                    ? 'border-cyan-400 text-navy-800'
                    : 'border-transparent text-gray-400 hover:text-navy-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">

        {loading ? (
          <div className="space-y-6">
            <div className="h-40 bg-gray-100 rounded-2xl animate-pulse" />
            <div className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
          </div>
        ) : (

          <>
            {/* ── ABOUT TAB ──────────────────────────────────── */}
            {activeTab === 'about' && (
              <div className="grid lg:grid-cols-3 gap-10">

                {/* Main text */}
                <div className="lg:col-span-2 space-y-8">

                  {/* About section */}
                  {info?.about && (
                    <div>
                      <h2 className="text-xl font-bold text-navy-800 mb-4 flex items-center gap-2">
                        <AcademicCapIcon className="h-6 w-6 text-cyan-500" />
                        About JNF Islamiyah
                      </h2>
                      <p className="text-gray-600 leading-relaxed whitespace-pre-line text-sm md:text-base">
                        {info.about}
                      </p>
                    </div>
                  )}

                  {/* History section */}
                  {info?.history && (
                    <div>
                      <h2 className="text-xl font-bold text-navy-800 mb-4 flex items-center gap-2">
                        <span className="text-cyan-500 text-lg">📜</span>
                        Our History
                      </h2>
                      <p className="text-gray-600 leading-relaxed whitespace-pre-line text-sm md:text-base">
                        {info.history}
                      </p>
                    </div>
                  )}

                  {(!info?.about && !info?.history) && (
                    <div className="text-center py-12 text-gray-400">
                      <AcademicCapIcon className="h-12 w-12 mx-auto mb-3 text-gray-200" />
                      <p>About information coming soon, in sha Allah.</p>
                    </div>
                  )}
                </div>

                {/* Sidebar info card */}
                <div className="space-y-4">
                  <div className="bg-navy-700 rounded-2xl p-6 text-white">
                    <h3 className="font-bold text-cyan-400 text-sm uppercase tracking-wide mb-4">School Details</h3>
                    <div className="space-y-3">
                      {info?.founded_year && (
                        <div>
                          <p className="text-white/50 text-xs mb-0.5">Year Founded</p>
                          <p className="text-white font-semibold">{info.founded_year}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-white/50 text-xs mb-0.5">Total Branches</p>
                        <p className="text-white font-semibold">{branches.length} location{branches.length !== 1 ? 's' : ''}</p>
                      </div>
                      {cities.length > 0 && (
                        <div>
                          <p className="text-white/50 text-xs mb-1">Cities</p>
                          <div className="flex flex-wrap gap-1.5">
                            {cities.map(c => (
                              <span key={c} className="bg-white/10 text-white/80 text-xs px-2 py-0.5 rounded-full">{c}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {info?.phone && (
                        <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                          <PhoneIcon className="h-4 w-4 text-cyan-400 flex-shrink-0" />
                          <a href={`tel:${info.phone}`} className="text-white/80 text-sm hover:text-white">{info.phone}</a>
                        </div>
                      )}
                      {info?.email && (
                        <div className="flex items-center gap-2">
                          <EnvelopeIcon className="h-4 w-4 text-cyan-400 flex-shrink-0" />
                          <a href={`mailto:${info.email}`} className="text-white/80 text-sm hover:text-white break-all">{info.email}</a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* CTA to branches */}
                  {branches.length > 0 && (
                    <button
                      onClick={() => setActiveTab('branches')}
                      className="w-full btn-primary text-sm py-3 flex items-center justify-center gap-2"
                    >
                      <MapPinIcon className="h-4 w-4" />
                      View All {branches.length} Branches
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ── BRANCHES TAB ───────────────────────────────── */}
            {activeTab === 'branches' && (
              branches.length === 0 ? (
                <div className="text-center py-20 text-gray-400">
                  <MapPinIcon className="h-12 w-12 mx-auto mb-4 text-gray-200" />
                  <p className="font-medium">No branch locations listed yet.</p>
                  <p className="text-sm mt-1">Check back soon, in sha Allah.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                  {/* Branch list sidebar */}
                  <div className="space-y-2 lg:max-h-[680px] lg:overflow-y-auto lg:pr-1">
                    {/* City group labels */}
                    {cities.length > 1
                      ? cities.map(city => (
                          <div key={city}>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest px-2 mb-1 mt-3 first:mt-0">{city}</p>
                            {branches.filter(b => b.city === city).map(b => (
                              <BranchListItem key={b.id} branch={b} selected={selected} onSelect={setSelected} />
                            ))}
                          </div>
                        ))
                      : branches.map(b => (
                          <BranchListItem key={b.id} branch={b} selected={selected} onSelect={setSelected} />
                        ))
                    }
                  </div>

                  {/* Branch detail panel */}
                  {selected && (
                    <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                      {selected.cover_url ? (
                        <img
                          src={selected.cover_url}
                          alt={selected.name}
                          className="w-full h-52 object-cover"
                        />
                      ) : (
                        <div className="w-full h-32 bg-gradient-to-br from-navy-600 to-navy-900 flex items-center justify-center">
                          <AcademicCapIcon className="h-14 w-14 text-white/30" />
                        </div>
                      )}

                      <div className="p-6">
                        <h2 className="text-xl font-bold text-navy-800 mb-1">{selected.name}</h2>
                        {selected.name_ar && (
                          <p className="arabic text-navy-600 text-lg mb-3">{selected.name_ar}</p>
                        )}

                        {selected.description && (
                          <p className="text-gray-600 text-sm leading-relaxed mb-5 whitespace-pre-line">
                            {selected.description}
                          </p>
                        )}

                        {/* Contact details */}
                        <div className="space-y-2.5 text-sm">
                          {(selected.address || selected.city) && (
                            <div className="flex items-start gap-2.5">
                              <MapPinIcon className="h-4 w-4 text-cyan-500 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700">
                                {[selected.address, selected.city, selected.state, selected.country]
                                  .filter(Boolean).join(', ')}
                              </span>
                            </div>
                          )}
                          {selected.phone && (
                            <div className="flex items-center gap-2.5">
                              <PhoneIcon className="h-4 w-4 text-cyan-500 flex-shrink-0" />
                              <a href={`tel:${selected.phone}`} className="text-gray-700 hover:text-cyan-600 transition-colors">
                                {selected.phone}
                              </a>
                            </div>
                          )}
                          {selected.email && (
                            <div className="flex items-center gap-2.5">
                              <EnvelopeIcon className="h-4 w-4 text-cyan-500 flex-shrink-0" />
                              <a href={`mailto:${selected.email}`} className="text-cyan-600 hover:underline">
                                {selected.email}
                              </a>
                            </div>
                          )}
                        </div>

                        {/* Teachers */}
                        {selected.teachers && selected.teachers.length > 0 && (
                          <div className="mt-6 pt-5 border-t border-gray-100">
                            <h3 className="text-sm font-bold text-navy-700 mb-3 flex items-center gap-2">
                              <UserIcon className="h-4 w-4 text-cyan-500" />
                              Teaching Staff
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              {selected.teachers.map(t => (
                                <span
                                  key={t.id}
                                  className="inline-flex items-center gap-1.5 bg-navy-50 border border-navy-100 text-navy-700 text-sm font-medium px-3 py-1.5 rounded-full"
                                >
                                  <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
                                  {t.name}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
          </>
        )}
      </div>
    </PublicLayout>
  );
}

// ── Reusable branch list item ─────────────────────────────────
function BranchListItem({ branch, selected, onSelect }) {
  const isSelected = selected?.id === branch.id;
  return (
    <button
      onClick={() => onSelect(branch)}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all mb-1 ${
        isSelected
          ? 'border-cyan-400 bg-cyan-50'
          : 'border-gray-100 bg-white hover:border-navy-200 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden ${isSelected ? 'bg-cyan-100' : 'bg-navy-100'}`}>
          {branch.cover_url
            ? <img src={branch.cover_url} alt="" className="w-full h-full object-cover" />
            : <AcademicCapIcon className={`h-5 w-5 ${isSelected ? 'text-cyan-600' : 'text-navy-400'}`} />
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold text-sm leading-tight ${isSelected ? 'text-navy-800' : 'text-navy-700'}`}>
            {branch.name}
          </p>
          {branch.name_ar && (
            <p className="arabic text-navy-500 text-xs mt-0.5">{branch.name_ar}</p>
          )}
          <p className="text-gray-400 text-xs mt-0.5 truncate">
            {[branch.address, branch.city].filter(Boolean).join(', ')}
          </p>
        </div>
      </div>
    </button>
  );
}
